import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';

import * as CustomerDetailConditionsActions from '../conditions/store/customer-conditions-actions';
import * as CustomerDetailExternalCustomerMappingActions from '../external-customer-mapping/store/customer-external-customer-mapping-actions';
import * as CustomerDetailOverviewActions from '../overview/store/customer-detail-overview-actions';
import { Api } from '../../../../../api';
import { AppState } from '../../../../../store/models/app-state';
import { ReduxDispatch } from '../../../../../store/use-thunk-dispatch';
import { SaveResult } from '../../../../../common/models/save-result';
import { customerDetailConditionsValidationSchema } from '../conditions/customer-detail-conditions-validation-schema';
import { customerDetailIsValidSelector } from './customer-detail-selectors';
import { PALO_MASTER_DATA_LABELS } from '../../../../../common/components/palo/palo-master-data-labels';
import { paloMissingFieldsMessage } from '../../../../../common/components/palo/palo-missing-fields-message';
import { paloSchemaErrors } from '../../../../../common/components/palo/palo-schema-errors';

export const CUSTOMER_DETAIL_RESET = 'CUSTOMER_DETAIL_RESET';
export const CUSTOMER_DETAIL_SAVE = 'CUSTOMER_DETAIL_SAVE';
export const CUSTOMER_DETAIL_DELETE = 'CUSTOMER_DETAIL_DELETE';
export const CUSTOMER_DETAIL_MEMBER_UPDATE = 'CUSTOMER_DETAIL_MEMBER_UPDATE';
export const CUSTOMER_DETAIL_TOGGLE_LOCK_CUSTOMER = 'CUSTOMER_DETAIL_TOGGLE_LOCK_CUSTOMER';

export const resetCustomerDetails = () => ({ type: CUSTOMER_DETAIL_RESET });

export const updateCustomerMember = (isMember: boolean, memberSince: Date | null) => {
    return {
        type: CUSTOMER_DETAIL_MEMBER_UPDATE,
        isMember,
        memberSince,
    };
};

export const deleteCustomer = () => {
    return async (dispatch: ReduxDispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: CUSTOMER_DETAIL_DELETE });

        const id = getState().contactsAndOrganizations.customers.customerDetail.overview.customer?.id;
        if (!id) {
            return;
        }

        const response = await api.contactsAndOrganizations.Customer.CustomerDetail.delete(id);
        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.customerDeleted')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const toggleLockCustomer = () => {
    return async (dispatch: ReduxDispatch, getState: () => AppState, { api }: { api: Api }) => {
        const customer = getState().contactsAndOrganizations.customers.customerDetail.overview.customer;
        if (!customer?.id) {
            return;
        }
        dispatch({ type: CUSTOMER_DETAIL_TOGGLE_LOCK_CUSTOMER });

        const response = customer.isLocked
            ? await api.contactsAndOrganizations.Customer.CustomerDetail.unlockCustomer(customer.id)
            : await api.contactsAndOrganizations.Customer.CustomerDetail.lockCustomer(customer.id);

        if (response.success) {
            dispatch(
                customer.isLocked
                    ? Snackbar.enqueue(
                          PoolNotification.success('app.contactsAndOrganizations.customer.unlockCustomer.success')
                      )
                    : Snackbar.enqueue(
                          PoolNotification.success('app.contactsAndOrganizations.customer.lockCustomer.success')
                      )
            );
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

/**
 * What the conditions tab calls its fields.
 *
 * The message falls back to the field name where no `app.label.<field>` exists,
 * and none of these have one — the refusal read "Es fehlen: Konditionen:
 * taxRateId, paymentTermId, ledgerAccountId". These are the ids the tab itself
 * puts above the inputs, so the message and the form say the same word.
 */
const CONDITION_LABELS: Record<string, string> = {
    taxRateId: 'app.common.counterAccount',
    paymentTermId: 'app.contactsAndOrganizations.detail.paymentTerm',
    paymentMethod: 'app.contactsAndOrganizations.detail.paymentMethod',
    ledgerAccountId: 'app.contactsAndOrganizations.conditions.ledgerAccount',
    bonusBase: 'app.contactsAndOrganizations.conditions.bonus.bonusBase',
    memberSince: 'app.contactsAndOrganizations.conditions.memberSince',
    discount: 'app.contactsAndOrganizations.conditions.dlh.discount',
    base: 'app.contactsAndOrganizations.conditions.dlh.base',
    bulk: 'app.contactsAndOrganizations.conditions.dlh.bulk',
};

export const saveCustomerDetails = () => {
    return async (dispatch: ReduxDispatch, getState: () => AppState) => {
        dispatch({ type: CUSTOMER_DETAIL_SAVE });

        const state = getState();
        const isAllValid = customerDetailIsValidSelector(state);
        const customerState = state.contactsAndOrganizations.customers.customerDetail.overview;
        const conditionsState = state.contactsAndOrganizations.customers.customerDetail.conditions;
        const externalMappingState =
            state.contactsAndOrganizations.customers.customerDetail.supplierExternalCustomerMappings;
        const customerId = state.contactsAndOrganizations.customers.customerDetail.overview.customer?.id;

        // The conditions tab reports itself only once it has been mounted, so a
        // member could be saved without a Gegenkonto as long as nobody opened
        // it (#2212, R-28). Its values are in the store from the moment the
        // record is loaded; this applies the tab's own rules to them from
        // here. A member that has no id yet cannot reach the tab, so nothing
        // is asked of it.
        //
        // Measured before switching this on: of 310 members, one lacks a
        // payment term and none a Gegenkonto. The same step on the supplier
        // mask would refuse two thirds of all suppliers, so it is not taken
        // there — see the ticket.
        const conditionErrors = customerId
            ? paloSchemaErrors(customerDetailConditionsValidationSchema, conditionsState.conditions)
            : {};
        const conditionsComplete = 0 === Object.keys(conditionErrors).length;

        if (!isAllValid || !conditionsComplete) {
            // Which fields, and on which tab — see paloMissingFieldsMessage.
            const missing = paloMissingFieldsMessage([
                {
                    tab: 'app.contactsAndOrganizations.detail.tab.details',
                    errors: customerState.overviewErrors,
                    labels: { ...PALO_MASTER_DATA_LABELS, name: 'app.label.organisationName' },
                },
                {
                    tab: 'app.contactsAndOrganizations.detail.tab.conditions',
                    errors: {
                        ...conditionErrors,
                        ...state.contactsAndOrganizations.customers.customerDetail.conditions.errors,
                    },
                    labels: CONDITION_LABELS,
                },
            ]);

            dispatch(
                Snackbar.enqueue(
                    missing
                        ? PoolNotification.error('app.snackbar.formErrorsNamed', { fields: missing })
                        : PoolNotification.error('app.snackbar.formErrors')
                )
            );
            return;
        }

        const promises = []; // : Promise<SaveResult | PayloadAction<SuccessResult<ExternalCustomerMappingFullResponse[]>>>[]

        if (customerState.isDirty) {
            promises.push(dispatch(CustomerDetailOverviewActions.saveCustomerDetail()));
        }
        if (conditionsState.isDirty) {
            promises.push(dispatch(CustomerDetailConditionsActions.saveConditions()));
        }
        if (externalMappingState.isDirty && customerId && externalMappingState.externalCustomerMapping) {
            promises.push(
                dispatch(
                    CustomerDetailExternalCustomerMappingActions.saveExternalCustomerMappings({
                        customerId,
                        externalCustomerMappings: externalMappingState.externalCustomerMapping,
                    })
                )
            );
        }

        // dispatch other save actions here

        // Nothing dirty means nothing was written. On a record that exists that
        // is simply a no-op, but on a new one it means the form was never
        // filled in — and "Gespeichert" would claim a record that does not
        // exist (#2212, seen on an untouched new person).
        if (0 === promises.length) {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.formErrors')));

            return;
        }

        const results = await Promise.all(promises);
        if (
            results.every(result =>
                typeof result === 'object' && 'meta' in result
                    ? result.meta?.requestStatus === 'fulfilled'
                    : result === SaveResult.Success
            )
        ) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.saved')));
        } else if (results.some(result => result === SaveResult.FormErrors)) {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.formErrors')));
        } else if (!results.some(result => result === SaveResult.ErrorReported)) {
            // Whoever returned ErrorReported has already said what went wrong.
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};
